import React, { useState, useEffect, useRef } from 'react';
import { Icon, Badge, Tooltip } from 'antd';
import { Link, Redirect } from 'react-router-dom';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { useAppContext } from '~/AppContext';

import { getMenuItems } from './menuItems';
import { fetchConferenceSummary } from '~/_domains/conference/helpers';
import { PERMISSIONS } from '~/constants/permissions.constants';
import logoIcon from '~/components/UI/Layout/images/icon.png';

var RAIL_WIDTH = 88;
var FLYOUT_WIDTH = 320;

/* ─── Section definitions: icon, color, order ─── */
var SECTION_META = {
  '_dashboard': { icon: 'dashboard', title: 'Dashboard', color: '#60a5fa', bg: 'rgba(96, 165, 250, 0.12)', order: 0 },
  'Gestão': { icon: 'team', title: 'Clientes', color: '#34d399', bg: 'rgba(52, 211, 153, 0.12)', order: 1 },
  '_tarefas': { icon: 'schedule', title: 'Tarefas', color: '#f472b6', bg: 'rgba(244, 114, 182, 0.12)', order: 2 },
  '_financeiro': { icon: 'dollar', title: 'Financeiro', color: '#a78bfa', bg: 'rgba(167, 139, 250, 0.12)', order: 3 },
  'Compliance': { icon: 'audit', title: 'Compliance', color: '#2dd4bf', bg: 'rgba(45, 212, 191, 0.12)', order: 4 },
  'Comercial': { icon: 'rise', title: 'Comercial', color: '#38bdf8', bg: 'rgba(56, 189, 248, 0.12)', order: 5 },
  'Marketing': { icon: 'notification', title: 'Marketing', color: '#fb923c', bg: 'rgba(251, 146, 60, 0.12)', order: 6 },
  '_relatorios': { icon: 'bar-chart', title: 'Relatórios', color: '#fbbf24', bg: 'rgba(251, 191, 36, 0.12)', order: 7 },
  'Recursos': { icon: 'cloud-upload', title: 'Recursos', color: '#c084fc', bg: 'rgba(192, 132, 252, 0.12)', order: 8 },
  '_outros': { icon: 'ellipsis', title: 'Outros', color: '#94a3b8', bg: 'rgba(148, 163, 184, 0.12)', order: 9 },
};

/* ─── Icon Rail (primary sidebar) ─── */

var RailLogoArea = styled.div`
  width: 100%;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  margin-bottom: 8px;
`;

var Rail = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: ${RAIL_WIDTH}px;
  background: #0f172a;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 0 8px;
  z-index: 1002;
  overflow-y: auto;
  overflow-x: hidden;
  border-right: 1px solid rgba(255, 255, 255, 0.06);

  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

var RailItem = styled.div`
  width: 74px;
  padding: 10px 0 8px;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: ${function(props) { return props.disabled ? 'not-allowed' : 'pointer'; }};
  opacity: ${function(props) { return props.disabled ? 0.35 : 1; }};
  background: ${function(props) { return props.active ? (props.activeBg || 'rgba(139, 92, 246, 0.2)') : 'transparent'; }};
  transition: all 0.15s ease;
  margin-bottom: 4px;
  position: relative;
  flex-shrink: 0;

  &:hover {
    background: ${function(props) { return props.disabled ? 'transparent' : (props.hoverBg || 'rgba(139, 92, 246, 0.12)'); }};
  }
`;

var RailIconWrap = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${function(props) { return props.bg || 'transparent'; }};
  transition: all 0.15s ease;

  i, .anticon {
    font-size: 20px;
    color: ${function(props) { return props.iconColor || 'rgba(255, 255, 255, 0.5)'; }};
  }
`;

var RailLabel = styled.span`
  font-size: 10px;
  font-weight: 500;
  color: ${function(props) { return props.active ? '#fff' : 'rgba(255, 255, 255, 0.5)'; }};
  text-align: center;
  line-height: 1.2;
  max-width: 74px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${RailItem}:hover & {
    color: ${function(props) { return props.disabled ? 'rgba(255, 255, 255, 0.3)' : '#fff'; }};
  }
`;

var ActiveIndicator = styled.div`
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 28px;
  border-radius: 0 3px 3px 0;
  background: ${function(props) { return props.color || '#8b5cf6'; }};
`;

var RailDivider = styled.div`
  width: 48px;
  height: 1px;
  background: rgba(255, 255, 255, 0.06);
  margin: 4px 0;
  flex-shrink: 0;
`;

var pulseDotAnimation = 'conference-pulse-dot';

var PulseDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #f5222d;
  margin-left: 8px;
  flex-shrink: 0;
  animation: ${pulseDotAnimation} 1.2s infinite;

  @keyframes ${pulseDotAnimation} {
    0% { box-shadow: 0 0 0 0 rgba(245, 34, 45, 0.6); background: #f5222d; }
    50% { background: #faad14; }
    70% { box-shadow: 0 0 0 6px rgba(245, 34, 45, 0); }
    100% { box-shadow: 0 0 0 0 rgba(245, 34, 45, 0); background: #f5222d; }
  }
`;

var RailBadge = styled.div`
  position: absolute;
  top: 6px;
  right: 6px;
  min-width: 16px;
  height: 16px;
  border-radius: 8px;
  background: #f5222d;
  color: white;
  font-size: 10px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
`;

/* ─── Flyout Panel (secondary sidebar) ─── */

var FlyoutOverlay = styled.div`
  position: fixed;
  top: 0;
  left: ${RAIL_WIDTH}px;
  right: 0;
  bottom: 0;
  z-index: 1002;
  display: ${function(props) { return props.visible ? 'block' : 'none'; }};
`;

var FlyoutPanel = styled.div`
  position: fixed;
  top: 0;
  left: ${RAIL_WIDTH}px;
  bottom: 0;
  width: ${FLYOUT_WIDTH}px;
  background: #1e293b;
  border-right: 1px solid rgba(255, 255, 255, 0.06);
  z-index: 1003;
  display: flex;
  flex-direction: column;
  transform: ${function(props) { return props.visible ? 'translateX(0)' : 'translateX(-8px)'; }};
  opacity: ${function(props) { return props.visible ? 1 : 0; }};
  pointer-events: ${function(props) { return props.visible ? 'auto' : 'none'; }};
  transition: all 0.2s ease;
  box-shadow: ${function(props) { return props.visible ? '4px 0 24px rgba(0, 0, 0, 0.3)' : 'none'; }};
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(139, 92, 246, 0.15) transparent;

  &::-webkit-scrollbar { width: 5px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: rgba(139, 92, 246, 0.2); border-radius: 3px; }
`;

var FlyoutHeader = styled.div`
  height: 64px;
  padding: 0 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.02);
`;

var FlyoutTitle = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  letter-spacing: 0.2px;
`;

var FlyoutHeaderIcon = styled.span`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${function(props) { return props.bg || 'rgba(139, 92, 246, 0.15)'; }};

  i, .anticon {
    font-size: 18px;
    color: ${function(props) { return props.iconColor || '#8b5cf6'; }};
  }
`;

var FlyoutBody = styled.div`
  padding: 8px 0;
  flex: 1;
`;

var FlyoutGroupLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.35);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 14px 16px 6px;
`;

var FlyoutSectionLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 14px 16px 6px;
`;

var FlyoutItem = styled.div`
  display: flex;
  align-items: center;
  padding: 0 14px;
  margin: 2px 8px;
  height: 42px;
  border-radius: 8px;
  cursor: ${function(props) { return props.disabled ? 'not-allowed' : 'pointer'; }};
  opacity: ${function(props) { return props.disabled ? 0.4 : 1; }};
  background: ${function(props) { return props.active ? 'rgba(139, 92, 246, 0.2)' : 'transparent'; }};
  transition: all 0.12s ease;

  &:hover {
    background: ${function(props) { return props.disabled ? 'transparent' : 'rgba(139, 92, 246, 0.1)'; }};
  }

  a {
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
    color: ${function(props) { return props.active ? '#c4b5fd' : 'rgba(255, 255, 255, 0.8)'; }};
    text-decoration: none;
    font-size: 14px;
    font-weight: ${function(props) { return props.active ? '500' : '400'; }};

    &:hover {
      color: ${function(props) { return props.disabled ? 'rgba(255, 255, 255, 0.4)' : '#e9d5ff'; }};
    }
  }

  i, .anticon {
    margin-right: 10px;
    font-size: 15px;
    color: ${function(props) { return props.active ? '#a78bfa' : 'rgba(255, 255, 255, 0.45)'; }};
  }
`;

var FlyoutItemTitle = styled.span`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

var FlyoutBadge = styled.span`
  margin-left: 8px;
`;

/* ─── Helpers ─── */

// Build rich tooltip content
var buildTooltip = function(menuItem) {
  if (!menuItem.description) return menuItem.title;
  return React.createElement('div', { style: { maxWidth: 220 } },
    React.createElement('div', { style: { fontWeight: 600, fontSize: 13, marginBottom: 4 } }, menuItem.title),
    React.createElement('div', { style: { fontSize: 12, opacity: 0.85, lineHeight: '1.4' } }, menuItem.description)
  );
};

// Determine which section a menu item belongs to
var getSectionKey = function(item, currentSection) {
  if (item.key === '/' || item.key === '/dashboard' || item.icon === 'dashboard') return '_dashboard';
  // Tarefas is its own section
  if (item.key === 'tarefas') return '_tarefas';
  // Solicitações goes under Tarefas (now inside subMenu, but keep for safety)
  if (item.key === '/solicitacoes') return '_tarefas';
  // Compliance items (formerly Tributário + Societário)
  if (item.key === 'compliance') return 'Compliance';
  if (item.key === 'malha-fiscal') return 'Compliance';
  if (item.key === '/IRPF') return 'Compliance';
  if (item.key === '/dividas-tributarias') return 'Compliance';
  // Itens Enviados goes under Relatórios
  if (item.key === 'itensEnviados') return '_relatorios';
  // Financeiro is its own section
  if (item.key === 'financeiro') return '_financeiro';
  // Relatórios is its own section
  if (item.key === 'relatorios') return '_relatorios';
  // Recuperação Tributária goes under Compliance
  if (item.key === '/recuperacao-tributaria') return 'Compliance';
  // INSS Obra goes under Compliance
  if (item.key && item.key.indexOf('inss-obra') !== -1) return 'Compliance';
  // Plugins, Base de conhecimento, Links go under Recursos
  if (item.key === '/plugins/forms' || item.key === '/plugins/click-counter') return 'Recursos';
  if (item.key === '/base-de-conhecimento') return 'Recursos';
  if (item.key === 'links-shortcut') return 'Recursos';
  if (item.key && item.key.indexOf('category-') === 0) return 'Recursos';
  if (item.key && item.key.indexOf('link-') === 0) return 'Recursos';
  // Section label from menu item definition
  if (item.section && item.sectionLabel) return item.sectionLabel;
  if (currentSection) return currentSection;
  return 'Recursos';
};

// Group flat menu items into sections
var groupItemsIntoSections = function(items) {
  var sections = {};
  var sectionOrder = [];
  var currentSection = null;

  items.forEach(function(item) {
    var sectionKey = getSectionKey(item, currentSection);

    // Track section changes from sectionLabel
    if (item.section && item.sectionLabel) {
      currentSection = item.sectionLabel;
    }

    if (!sections[sectionKey]) {
      sections[sectionKey] = [];
      sectionOrder.push(sectionKey);
    }

    sections[sectionKey].push(item);
  });

  // Sort sections by defined order
  sectionOrder.sort(function(a, b) {
    var metaA = SECTION_META[a] || SECTION_META['_outros'];
    var metaB = SECTION_META[b] || SECTION_META['_outros'];
    return metaA.order - metaB.order;
  });

  return { sections: sections, order: sectionOrder };
};

/* ─── Main Component ─── */

var MainMenu = function(props) {
  var location = props.location;
  var topOffset = props.topOffset || 0;
  var history = useHistory();

  var state = useSelector(function(state) {
    return {
      user: state.user,
      links: state.links.list,
      linkCategories: state.links.categories,
      failedEmailsCount: state.emailsLogs.failed,
      solicitationsCount: state.solicitations.data.filter(function(s) { return s.status === 0 && !s.deleted_at; }).length,
      bankReconciliationPending: state.bankReconciliation && state.bankReconciliation.stats ? state.bankReconciliation.stats.pending : 0
    };
  });

  var context = useAppContext();
  var unlockedMenus = context.unlockedMenus;

  var conferenceUrgentState = useState(0);
  var conferenceUrgentCount = conferenceUrgentState[0];
  var setConferenceUrgentCount = conferenceUrgentState[1];

  var canSeeConference = state.user && state.user.permissions &&
    state.user.permissions.includes(PERMISSIONS.conference.view);

  useEffect(function() {
    if (!canSeeConference) return;
    var isMounted = true;
    fetchConferenceSummary().then(function(summary) {
      if (isMounted) setConferenceUrgentCount(summary.sirenPending);
    });
    var interval = setInterval(function() {
      fetchConferenceSummary(true).then(function(summary) {
        if (isMounted) setConferenceUrgentCount(summary.sirenPending);
      });
    }, 5 * 60 * 1000);
    return function() { isMounted = false; clearInterval(interval); };
  }, [canSeeConference]);

  var menuItems = getMenuItems(state.user, state.links, state.linkCategories, state.failedEmailsCount, state.solicitationsCount, unlockedMenus, state.bankReconciliationPending, conferenceUrgentCount);

  // Flyout state
  var flyoutState = useState(null);
  var activeFlyout = flyoutState[0];
  var setActiveFlyout = flyoutState[1];

  // Check if dashboard is available and redirect if needed
  var dashboardAvailable = menuItems.find(function(item) { return item.link === '/'; });
  if (location.pathname === '/' && !dashboardAvailable && menuItems.length > 0) {
    return React.createElement(Redirect, { to: menuItems[0].link });
  }

  var sortedItems = menuItems.slice().sort(function(a, b) { return a.disabled - b.disabled; });

  // Flatten menu items for search
  var flattenMenuItems = function(items) {
    var result = [];
    items.forEach(function(item) {
      if (item.subMenu) {
        item.subMenu.forEach(function(sub) {
          if (sub.subMenu) {
            sub.subMenu.forEach(function(nested) {
              if (nested.link) {
                result.push(Object.assign({}, nested, { icon: nested.icon || sub.icon || item.icon }));
              }
            });
          } else if (sub.link) {
            result.push(Object.assign({}, sub, { icon: sub.icon || item.icon }));
          }
        });
      } else if (item.link) {
        result.push(item);
      }
    });
    return result;
  };

  // Expose to parent via effect
  var menuItemsRef = useRef(menuItems);
  menuItemsRef.current = menuItems;

  useEffect(function() {
    if (props.onMenuReady) {
      props.onMenuReady(menuItemsRef.current, flattenMenuItems);
    }
  }, [menuItems.length]);

  // Group items into sections
  var grouped = groupItemsIntoSections(sortedItems);

  // Check if any item in a section is active
  var isSectionActive = function(sectionItems) {
    return sectionItems.some(function(item) {
      if (item.link && location.pathname === item.link) return true;
      if (item.subMenu) {
        return item.subMenu.some(function(sub) {
          if (sub.link && location.pathname === sub.link) return true;
          if (sub.subMenu) {
            return sub.subMenu.some(function(nested) {
              return nested.link && location.pathname === nested.link;
            });
          }
          return false;
        });
      }
      return false;
    });
  };

  function sectionHasPulseDot(items) {
    return items.some(function(item) {
      if (item.pulseDot) return true;
      return !!(item.subMenu && item.subMenu.some(function(sub) { return sub.pulseDot; }));
    });
  }

  // Get total badge count for a section
  var getSectionBadge = function(sectionItems) {
    var total = 0;
    sectionItems.forEach(function(item) {
      if (item.badge) total += item.badge;
    });
    return total;
  };

  var handleRailClick = function(sectionKey, sectionItems) {
    // Dashboard: navigate directly
    if (sectionKey === '_dashboard') {
      setActiveFlyout(null);
      var dashItem = sectionItems[0];
      if (dashItem && dashItem.link && !dashItem.disabled) {
        history.push(dashItem.link);
      }
      return;
    }

    // If section has only one item with no submenu, navigate directly
    if (sectionItems.length === 1 && !sectionItems[0].subMenu && sectionItems[0].link) {
      setActiveFlyout(null);
      if (!sectionItems[0].disabled) {
        history.push(sectionItems[0].link);
      }
      return;
    }

    // Toggle flyout
    if (activeFlyout && activeFlyout.key === sectionKey) {
      setActiveFlyout(null);
    } else {
      setActiveFlyout({ key: sectionKey, items: sectionItems });
    }
  };

  var handleRailMouseEnter = function(sectionKey, sectionItems) {
    // Dashboard or single direct-link: close flyout
    if (sectionKey === '_dashboard' || (sectionItems.length === 1 && !sectionItems[0].subMenu && sectionItems[0].link)) {
      setActiveFlyout(null);
      return;
    }
    setActiveFlyout({ key: sectionKey, items: sectionItems });
  };

  var closeFlyout = function() {
    setActiveFlyout(null);
  };

  // Render rail items (one per section)
  var railElements = [];

  // Logo at top
  railElements.push(
    React.createElement(RailLogoArea, { key: '_logo' },
      React.createElement('img', {
        src: logoIcon,
        alt: 'Contlabs',
        style: { height: 28 }
      })
    )
  );

  // Filter out empty sections (all items removed by permission filtering)
  var visibleSections = grouped.order.filter(function(sectionKey) {
    var items = grouped.sections[sectionKey];
    return items && items.length > 0;
  });

  visibleSections.forEach(function(sectionKey, idx) {
    var sectionItems = grouped.sections[sectionKey];
    var meta = SECTION_META[sectionKey] || SECTION_META['_outros'];
    var isActive = isSectionActive(sectionItems);
    var isFlyoutOpen = activeFlyout && activeFlyout.key === sectionKey;
    var badge = getSectionBadge(sectionItems);

    if (idx > 0 && (sectionKey === 'Compliance' || sectionKey === 'Comercial' || sectionKey === '_relatorios' || sectionKey === 'Recursos')) {
      railElements.push(React.createElement(RailDivider, { key: 'div-' + sectionKey }));
    }

    railElements.push(
      React.createElement(RailItem, {
        key: sectionKey,
        active: isActive || isFlyoutOpen,
        activeBg: meta.bg,
        hoverBg: meta.bg,
        onClick: function() { handleRailClick(sectionKey, sectionItems); },
        onMouseEnter: function() { handleRailMouseEnter(sectionKey, sectionItems); },
      },
        isActive && React.createElement(ActiveIndicator, { color: meta.color }),
        React.createElement(RailIconWrap, {
          bg: (isActive || isFlyoutOpen) ? meta.bg : 'rgba(255, 255, 255, 0.04)',
          iconColor: (isActive || isFlyoutOpen) ? meta.color : 'rgba(255, 255, 255, 0.45)'
        },
          React.createElement(Icon, { type: meta.icon })
        ),
        React.createElement(RailLabel, {
          active: isActive || isFlyoutOpen
        }, meta.title),
        badge > 0 && React.createElement(RailBadge, null, badge > 99 ? '99+' : badge),
        sectionHasPulseDot(sectionItems) && React.createElement(PulseDot, {
          style: { position: 'absolute', top: 10, right: 14, marginLeft: 0 }
        })
      )
    );
  });

  // Render flyout panel
  var flyoutElement = null;
  if (activeFlyout && activeFlyout.items) {
    var flyoutMeta = SECTION_META[activeFlyout.key] || SECTION_META['_outros'];
    var flyoutChildren = [];

    var totalItems = activeFlyout.items.length;

    activeFlyout.items.forEach(function(item) {
      if (item.subMenu && item.subMenu.length > 0) {
        // Only show group label if multiple items and title differs from section
        if (totalItems > 1 && item.title !== flyoutMeta.title) {
          flyoutChildren.push(
            React.createElement(FlyoutGroupLabel, { key: 'group-' + item.key }, item.title)
          );
        }

        // Render sub-items
        item.subMenu.forEach(function(subItem) {
          if (subItem.section && subItem.sectionLabel) {
            flyoutChildren.push(
              React.createElement(FlyoutSectionLabel, { key: 'sec-' + subItem.key }, subItem.sectionLabel)
            );
          }

          var isSubActive = location.pathname === subItem.link;

          flyoutChildren.push(
            React.createElement(Tooltip, {
              key: subItem.key,
              title: buildTooltip(subItem),
              placement: 'right',
              mouseEnterDelay: 0.4
            },
              React.createElement(FlyoutItem, {
                active: isSubActive,
                disabled: subItem.disabled,
                onClick: function() { if (!subItem.disabled) setActiveFlyout(null); }
              },
                React.createElement(Link, { to: subItem.disabled ? '#' : (subItem.link || '#') },
                  subItem.iconImg
                    ? subItem.iconImg
                    : (subItem.icon && React.createElement(Icon, { type: subItem.icon })),
                  React.createElement(FlyoutItemTitle, null, subItem.title),
                  subItem.pulseDot && React.createElement(PulseDot),
                  subItem.badge > 0 && React.createElement(FlyoutBadge, null,
                    React.createElement(Badge, { count: subItem.badge, style: { zoom: 0.8 } })
                  )
                )
              )
            )
          );
        });
      } else if (item.link) {
        // Direct link items
        var isItemActive = location.pathname === item.link;

        flyoutChildren.push(
          React.createElement(Tooltip, {
            key: item.key,
            title: buildTooltip(item),
            placement: 'right',
            mouseEnterDelay: 0.4
          },
            React.createElement(FlyoutItem, {
              active: isItemActive,
              disabled: item.disabled,
              onClick: function() { if (!item.disabled) setActiveFlyout(null); }
            },
              React.createElement(Link, { to: item.disabled ? '#' : item.link },
                item.iconImg
                  ? item.iconImg
                  : (item.icon && React.createElement(Icon, { type: item.icon })),
                React.createElement(FlyoutItemTitle, null, item.title),
                item.pulseDot && React.createElement(PulseDot),
                item.badge > 0 && React.createElement(FlyoutBadge, null,
                  React.createElement(Badge, { count: item.badge, style: { zoom: 0.8 } })
                )
              )
            )
          )
        );
      }
    });

    flyoutElement = React.createElement(React.Fragment, null,
      React.createElement(FlyoutOverlay, { visible: true, onClick: closeFlyout, style: topOffset ? { top: topOffset } : undefined }),
      React.createElement(FlyoutPanel, {
        visible: true,
        onMouseLeave: closeFlyout,
        style: topOffset ? { top: topOffset } : undefined
      },
        React.createElement(FlyoutHeader, null,
          React.createElement(FlyoutHeaderIcon, {
            bg: flyoutMeta.bg,
            iconColor: flyoutMeta.color
          },
            React.createElement(Icon, { type: flyoutMeta.icon })
          ),
          React.createElement(FlyoutTitle, null, flyoutMeta.title)
        ),
        React.createElement(FlyoutBody, null, flyoutChildren)
      )
    );
  }

  return React.createElement(React.Fragment, null,
    React.createElement(Rail, { style: topOffset ? { top: topOffset } : undefined }, railElements),
    flyoutElement
  );
};

export default MainMenu;
export { RAIL_WIDTH };
